import { App } from "@/constants";
import { CURRENT_APP_QUERY_PARAMETER } from "@/constants/current_app";
import { gwStorefrontAppInfoToken } from "@/tokens";
import ApiClient from "@lib/ApiClient";
import ApiClientWithMetrics from "@lib/ApiClient/ApiClientWithMetrics";
import AuthApiClient from "@lib/ApiClient/AuthApiClient";
import { MinilogLogger } from "@lib/Logger";
import { AnalyticsEventsCollectorModule } from "@modules/analytics_events_collector/AnalyticsEventsCollectorModule";
import { BaseModule } from "@modules/base_module/BaseModule";
import { MODULE_STATUS } from "@modules/base_module/ModulesStatusesProvider/types";
import { CartModule } from "@modules/cart/CartModule";
import { CommonModule } from "@modules/common_module/CommonModule";
import { LegacyTrackingModule } from "@modules/legacy_tracking/LegacyTrackingModule";
import { TokenManagerModule } from "@modules/token_manager/TokenManagerModule";
import MutationObserverInjector from "@ui-utils/Injector/MutationObserverInjector";
import { assert } from "superstruct";
import { container, inject, instanceCachingFactory, singleton } from "tsyringe";

import { ApplicationInterface } from "@interfaces/ApplicationInterface";

import {
    InstagramApplicationConfigInterface,
    InstagramApplicationConfigSchema,
    InstagramApplicationConfigToken,
} from "./InstagramApplicationConfig";
import {
    instagramApplicationLoggerToken,
    instagramAuthApiClientToken,
    instagramAuthApiV3ClientToken,
} from "./tokens";
import InstagramAddToCartTracker from "./utils/InstagramAddToCartTracker";
import InstagramGalleryWidgetInjector from "./widgets/instagram_gallery_widget/InstagramGalleryWidgetInjector";
import LegacyInstagramGalleryWidgetInjector from "./widgets/instagram_gallery_widget/LegacyInstagramGalleryWidgetInjector";

@singleton()
class InstagramApplication implements ApplicationInterface<object> {
    widgets = [];
    exports = {};
    constructor(
        @inject(InstagramApplicationConfigToken)
        private readonly config: InstagramApplicationConfigInterface,
        @inject(CartModule)
        private readonly cartModule: CartModule,
        @inject(CommonModule)
        private readonly commonModule: CommonModule,
        @inject(BaseModule)
        public readonly baseModule: BaseModule,
        @inject(LegacyTrackingModule)
        private readonly legacyTrackingModule: LegacyTrackingModule,
        @inject(MutationObserverInjector)
        private readonly mutationObserverInjector: MutationObserverInjector
    ) {
        assert(config, InstagramApplicationConfigSchema);
    }
    init(): this {
        const analyticEventsCollectorModule = container.resolve(
            AnalyticsEventsCollectorModule
        );

        const isDesignMode =
            this.commonModule.exports.designModeProvider.isDesignMode();

        const eventsCollector =
            analyticEventsCollectorModule.exports.eventsCollector;
        const analyticsEventsFactory =
            analyticEventsCollectorModule.exports.eventsFactory;
        const logger = new MinilogLogger("instagram");
        container.registerInstance(instagramApplicationLoggerToken, logger);
        container.register(instagramAuthApiClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/v2/"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode || false,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(instagramApplicationLoggerToken)
                        ),
                        eventsCollector,
                        this.commonModule.exports.locationController,
                        analyticsEventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });
        container.register(instagramAuthApiV3ClientToken, {
            useFactory: instanceCachingFactory((di) => {
                return new AuthApiClient(
                    new ApiClientWithMetrics(
                        new ApiClient(
                            this.commonModule.exports.baseUrlProvider.appendToBaseUrl(
                                "/storefront-api/v3/"
                            ),
                            undefined,
                            {
                                [CURRENT_APP_QUERY_PARAMETER]: di.resolve(
                                    gwStorefrontAppInfoToken
                                ).currentApp,
                                designMode: isDesignMode || false,
                                ...this.commonModule.exports
                                    .designModeQueryParamsProvider.queryParams,
                            },
                            di.resolve(instagramApplicationLoggerToken)
                        ),
                        eventsCollector,
                        this.commonModule.exports.locationController,
                        analyticsEventsFactory
                    ),
                    di.resolve(TokenManagerModule).exports.tokenManager
                );
            }),
        });

        const referrerProvider =
            container.resolve(CommonModule).exports.referrerProvider;
        logger.debug("Instagram application start initialization", {
            ...this.config,
        });

        // start injectors
        const injector = new InstagramGalleryWidgetInjector(
            this.config.placeholderSelector,
            logger,
            eventsCollector,
            analyticsEventsFactory,
            this.mutationObserverInjector
        );
        const legacyInjector = new LegacyInstagramGalleryWidgetInjector(
            logger,
            eventsCollector,
            analyticsEventsFactory,
            this.mutationObserverInjector
        );
        injector.start();
        legacyInjector.start();

        // cart listener
        const cartListener = this.cartModule.exports.cartListener;
        const cartService = this.cartModule.exports.cartService;
        const cartTracker = new InstagramAddToCartTracker(
            logger,
            cartListener,
            cartService,
            referrerProvider,
            this.legacyTrackingModule.exports.trackingApiService
        );
        cartTracker.track();

        this.baseModule.exports.modulesStatusesProvider.updateModuleStatus(
            App.Instagram,
            MODULE_STATUS.INITIALIZED
        );

        return this;
    }
}

export { InstagramApplication };
